import { ref } from 'vue';
import { useAuthStore } from '../stores/auth';
import { parseLogContent } from '../utils/logParser';
import type { LogLine } from '../utils/stageParser';

type LoadingState = 'idle' | 'loading' | 'loaded' | 'error';

/**
 * Composable for fetching and parsing historical build logs via HTTP
 * Used for finished builds instead of WebSocket streaming
 */
export function useHistoricalLogs(buildId: string) {
  const authStore = useAuthStore();
  const logs = ref<LogLine[]>([]);
  const loadingState = ref<LoadingState>('idle');
  const error = ref<string | null>(null);

  /**
   * Fetch logs from storage via presigned URL
   */
  async function fetchLogs(): Promise<void> {
    if (loadingState.value === 'loading') {
      return;
    }

    if (!authStore.token) {
      error.value = 'Authentication required';
      loadingState.value = 'error';
      return;
    }

    loadingState.value = 'loading';
    error.value = null;

    try {
      // Get presigned URL from backend
      const response = await fetch(`/api/builds/${buildId}/logs/content`, {
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          // No logs available yet
          logs.value = [];
          loadingState.value = 'loaded';
          return;
        }
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || `HTTP ${response.status}`);
      }

      const { contentUrl } = await response.json();
      if (!contentUrl) {
        throw new Error('No content URL returned');
      }

      // Fetch raw log content from S3 storage
      const contentResponse = await fetch(contentUrl);
      if (!contentResponse.ok) {
        throw new Error(`Failed to fetch logs: HTTP ${contentResponse.status}`);
      }

      const rawContent = await contentResponse.text();

      // Parse the log content
      logs.value = parseLogContent(rawContent);
      loadingState.value = 'loaded';
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch logs';
      loadingState.value = 'error';
    }
  }

  function clearLogs(): void {
    logs.value = [];
    loadingState.value = 'idle';
    error.value = null;
  }

  return {
    logs,
    loadingState,
    error,
    fetchLogs,
    clearLogs,
  };
}
